import * as IoC from 'typescript-ioc';
import { IConnectionTemplateConnectionGroupDTO, TConnection } from '../../../../../api/blueprints/connection.template.types';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../../../../infrastructure/sub-windows';
import { IReactIntlProvider } from '../../../../../infrastructure/react-intl-provider';
import { IntlShape } from 'react-intl';
import { action, observable } from 'mobx';
import { ADD_CONNECTION_GROUP_TITLE_TEXT, EDIT_CONNECTION_GROUP_TITLE_TEXT } from '../../../../_events/_manage-event/shared/connection-groups-list/connection-groups.texts';
import { ConnectionTemplatesFormVM } from '../connection-template-form.viewmodel';
import { ConnectionTemplateConnectionGroupForm } from './connection-group-from/connection-template-connection-group-form';
import { ConnectionGroupsPassportPreviewViewModel } from '../../../../_events/_manage-event/shared/connection-groups-list/connection-groups-passport-preview/connection-group-passport-preview.viewmodel';
import { IConfigurePassportDisplayFormProps } from '../../../../_events/_manage-event/shared/configure-passport-display/configure-passport-display.viewmodel';
import { IGlobalStorage } from '../../../../../infrastructure/global-storage';
import { PassportConfigurationMode } from '../../../../_events/_manage-event/shared/configure-passport-display/configure-passport-display.config';


class ConnectionTemplateConnectionGroupVM {
    @observable
    private newConnectionGroupSubWindow: ISubWindowFacade | null = null;
    @observable
    private groupIdToEditPopupMap: { [key: number]: ISubWindowFacade } = {};

    constructor(
        private readonly connectionTemplatesFormVM,
        @IoC.Inject private readonly subWindowService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
        @IoC.Inject protected readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage),
    ) {

    }

    public get formVM(): ConnectionTemplatesFormVM {
        return this.connectionTemplatesFormVM;
    }

    public get intl(): IntlShape {
        return this.intlProvider.getIntl();
    }

    public get connections(): TConnection[] {
        return this.formVM.connections;
    }

    public get groupList(): IConnectionTemplateConnectionGroupDTO[] {
        return this.formVM.connectionGroups;
    }

    public get isNewGroupPopupOpened(): boolean {
        return !!this.newConnectionGroupSubWindow && this.newConnectionGroupSubWindow.getStatus() !== SubWindowStatus.Closed;
    }

    public getOrderedConnections = (group: IConnectionTemplateConnectionGroupDTO): TConnection[] => {
        const connections = this.connections;
        return group.connections
            .slice()
            .sort((a, b) => a.order - b.order)
            // @ts-ignore
            .map(({ connectionId }) => connections.find(c => this.formVM.findCurrentConnection(c, connectionId)))
            .filter(Boolean)
        ;
    };

    public isGroupPopupOpen = (groupId: string): boolean => {
        const alreadyShownSubWindow = this.groupIdToEditPopupMap[groupId];
        return alreadyShownSubWindow && alreadyShownSubWindow.getStatus() !== SubWindowStatus.Closed;
    };

    @action.bound
    public openAddConnectionGroupPopup = (): void => {
        this.newConnectionGroupSubWindow = this.subWindowService.showSubWindow({
            title: this.intl.formatMessage(ADD_CONNECTION_GROUP_TITLE_TEXT),
            componentToHost: ConnectionTemplateConnectionGroupForm,
            componentToHostProps: {
                templateformVM: this.formVM
            }
        });
    };

    @action
    public openEditGroupPopup = (group: IConnectionTemplateConnectionGroupDTO): void => {
        if (this.isGroupPopupOpen(group.meetingConnectionId)) {
            this.groupIdToEditPopupMap[group.meetingConnectionId].maximize();
        } else {
            this.groupIdToEditPopupMap[group.meetingConnectionId] = this.subWindowService.showSubWindow({
                title: this.intl.formatMessage(EDIT_CONNECTION_GROUP_TITLE_TEXT),
                componentToHost: ConnectionTemplateConnectionGroupForm,
                componentToHostProps: {
                    group: group,
                    templateformVM: this.formVM,
                    closeGroupIdToEditPopup: this.closeGroupIdToEditPopup
                }
            });
        }
    };

    @action
    public onDeleteConnectionGroup = (meetingConnectionId: string): void => {
        this.closeGroupIdToEditPopup(meetingConnectionId);
        this.formVM.deleteConnectionGroup(meetingConnectionId);
    };

    public getPassportPreviewVM = (group: IConnectionTemplateConnectionGroupDTO): ConnectionGroupsPassportPreviewViewModel => {
        const orderedConnections = this.getOrderedConnections(group);
        const configurePassportDisplayFormProps = this.formConfigurePassportDisplayFormProps();
        return new ConnectionGroupsPassportPreviewViewModel(
            configurePassportDisplayFormProps,
            orderedConnections,
            group.label,
            PassportConfigurationMode.ConnectionTemplate
        );
    };

    private formConfigurePassportDisplayFormProps = (): IConfigurePassportDisplayFormProps => {
        return {
            eventId: this.globalStorage.getEvent()?.eventId,
            accountId: this.globalStorage.getEvent()?.accountId,
            meetingConnectionId: null,
        };
    };

    @action
    private closeGroupIdToEditPopup = (meetingConnectionId: string): void => {
        this.groupIdToEditPopupMap[meetingConnectionId]?.close();
    };
}

export default ConnectionTemplateConnectionGroupVM;
